"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import gsap from "gsap";
import { CustomEase } from "gsap/CustomEase";
import { SplitText } from "gsap/SplitText";
import Lenis from "lenis";
import Spline from "@splinetool/react-spline";
import { FiRefreshCw } from "react-icons/fi";
import { Link } from "@/i18n/routing";
/**
 * Minimal Push Overlay Menu
 * ---------------------------------------------------------
 * - The trigger renders inline wherever you place <MinimalPushOverlayMenu />
 *   (e.g. in your nav row) — a plain pill button reading "Menu". It only
 *   opens the overlay; it does not morph into a close icon.
 * - The overlay itself is rendered through a React portal straight into
 *   document.body, so it's a true full-viewport layer that has nothing to
 *   do with the nav's DOM nesting, transforms, or stacking context.
 * - The close control (bordered circle with an "X") lives inside the
 *   overlay itself, fixed to the top-right corner, so it's always visible
 *   exactly where the overlay is — not dependent on the nav's layout.
 *
 * Notes:
 * - `SplitText` / `CustomEase` are bundled with gsap 3.12+ (SplitText is
 *   fully free from 3.13 onward) — check your installed version.
 * - `lenis` is the npm package `lenis`.
 * - "PP Neue Montreal" is pulled from cdnfonts.com to mirror the original;
 *   self-host it for production instead.
 * - Only ONE <MinimalPushOverlayMenu /> should ever be mounted at a time —
 *   each instance owns its own Lenis instance and portal, so mounting it
 *   twice (e.g. once per responsive nav variant) will create two competing
 *   overlays.
 * - The Spline scene requires a working WebGL context. Some environments
 *   (sandboxed iframes, headless browsers, GPU-disabled setups, some
 *   corporate lockdowns) refuse to provide one. This component checks for
 *   WebGL support before mounting <Spline> and falls back to just the
 *   "Let's Talk" heading if it's unavailable, so the overlay never breaks.
 */

const NAV_LINKS = ["Index", "Portfolio", "Studio", "Journal", "Connect"];
const TAGS = ["Web Animations", "Interactive Media", "Motion Craft"];

import type { Application } from "@splinetool/runtime";

interface FormData {
  name: string;
  company: string;
  email: string;
  phone: string;
  message: string;
}

interface CaptchaChallenge {
  question: string;
  answer: number;
}

// Quick, cheap capability check — creates a throwaway canvas and asks for
// a webgl/experimental-webgl context without actually rendering anything.
function hasWebGL(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    return !!gl;
  } catch {
    return false;
  }
}

export default function Letstalk() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [webglOk, setWebglOk] = useState(true);
  const [splineError, setSplineError] = useState(false);

  const menuToggleBtnRef = useRef<HTMLDivElement>(null);
  const menuOverlayRef = useRef<HTMLDivElement>(null);
  const menuOverlayContentRef = useRef<HTMLDivElement>(null);
  const menuMediaWrapperRef = useRef<HTMLDivElement>(null);
  const menuColRefs = useRef<HTMLDivElement[]>([]);
  const splineRef = useRef<Application | null>(null);
  const isAnimatingRef = useRef(false);
  const isMenuOpenRef = useRef(false);
  const splitByContainerRef = useRef<SplitText[][]>([]);
  const lenisRef = useRef<Lenis | null>(null);

  // --- Contact form state -------------------------------------------------
  const [formData, setFormData] = useState<FormData>({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });

  const [captcha, setCaptcha] = useState<CaptchaChallenge>({ question: "", answer: 0 });
  const [captchaInput, setCaptchaInput] = useState<string>("");
  const [captchaValid, setCaptchaValid] = useState<boolean>(false);
  const [showCaptcha, setShowCaptcha] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<{ type: "success" | "error" | ""; message: string }>({
    type: "",
    message: "",
  });

  const generateCaptcha = () => {
    const operations = [
      { op: "+", fn: (a: number, b: number) => a + b },
      { op: "-", fn: (a: number, b: number) => a - b },
      { op: "×", fn: (a: number, b: number) => a * b },
    ];

    const num1 = Math.floor(Math.random() * 20) + 1;
    const num2 = Math.floor(Math.random() * 20) + 1;
    const selectedOp = operations[Math.floor(Math.random() * operations.length)];

    const answer = selectedOp.fn(num1, num2);
    const question = `${num1} ${selectedOp.op} ${num2} = ?`;

    setCaptcha({ question, answer });
    setCaptchaInput("");
    setCaptchaValid(false);
  };

  const validateCaptcha = (value: string) => {
    const isValid = parseInt(value) === captcha.answer;
    setCaptchaValid(isValid);
    return isValid;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCaptchaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCaptchaInput(value);
    validateCaptcha(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus({ type: "", message: "" });

    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      setStatus({ type: "error", message: "Please fill in all required fields." });
      return;
    }

    if (!showCaptcha) {
      setShowCaptcha(true);
      generateCaptcha();
      setStatus({ type: "", message: "Please solve the CAPTCHA to continue." });
      return;
    }

    if (!captchaValid) {
      setStatus({ type: "error", message: "Incorrect CAPTCHA answer. Please try again." });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({ type: "success", message: "Thanks! Your message has been sent." });
        setFormData({ name: "", company: "", email: "", phone: "", message: "" });
        setShowCaptcha(false);
        setCaptchaInput("");
        setCaptchaValid(false);
      } else {
        setStatus({ type: "error", message: data.error || "Something went wrong. Please try again." });
      }
    } catch (error) {
      setStatus({ type: "error", message: "Network error. Please try again later." });
    } finally {
      setLoading(false);
    }
  };
  // -------------------------------------------------------------------------

  const onSplineLoad = (spline: Application) => {
    splineRef.current = spline;
    spline.setBackgroundColor("transparent" as any);
  };

  const addColRef = (el: HTMLDivElement | null) => {
    if (el && !menuColRefs.current.includes(el)) {
      menuColRefs.current.push(el);
    }
  };

  // Portals need a browser document, so only render one after mount.
  // Also check WebGL support once we're client-side.
  useEffect(() => {
    setMounted(true);
    setWebglOk(hasWebGL());
  }, []);

  useEffect(() => {
    if (!mounted) return;

    gsap.registerPlugin(CustomEase, SplitText);
    CustomEase.create("hop", ".87,0,.13,1");

    const lenis = new Lenis();
    lenisRef.current = lenis;
    let rafId: number;
    function raf(time: number) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    // Split the text inside each .menu-col into masked lines
    const splitByContainer: SplitText[][] = [];
    menuColRefs.current.forEach((col) => {
      const textElements = col.querySelectorAll("a, p");
      const containerSplits: SplitText[] = [];

      textElements.forEach((element) => {
        const split = SplitText.create(element, {
          type: "lines",
          mask: "lines",
          linesClass: "menu-split-line",
        });
        containerSplits.push(split);
        gsap.set(split.lines, { y: "-110%" });
      });

      splitByContainer.push(containerSplits);
    });
    splitByContainerRef.current = splitByContainer;

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      splitByContainer.forEach((splits) => splits.forEach((s) => s.revert()));
    };
  }, [mounted]);

  const handleToggle = () => {
    if (isAnimatingRef.current) return;

    const menuOverlay = menuOverlayRef.current;
    const menuOverlayContent = menuOverlayContentRef.current;
    const menuMediaWrapper = menuMediaWrapperRef.current;
    const copyContainers = menuColRefs.current;
    const splitByContainer = splitByContainerRef.current;

    if (!menuOverlay || !menuOverlayContent || !menuMediaWrapper) {
      return;
    }

    if (!isMenuOpenRef.current) {
      isAnimatingRef.current = true;
      lenisRef.current?.stop();

      const tl = gsap.timeline();

      tl.to(menuOverlay, {
        clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
        duration: 1,
        ease: "hop",
      })
        .to(menuOverlayContent, { yPercent: 0, duration: 1, ease: "hop" }, "<")
        .to(
          menuMediaWrapper,
          { opacity: 1, duration: 0.75, ease: "power2.out", delay: 0.5 },
          "<"
        );

      splitByContainer.forEach((containerSplits) => {
        const copyLines = containerSplits.flatMap((split) => split.lines);
        tl.to(
          copyLines,
          { y: "0%", duration: 2, ease: "hop", stagger: -0.075 },
          -0.15
        );
      });

      tl.call(() => {
        isAnimatingRef.current = false;
      });

      isMenuOpenRef.current = true;
      setIsMenuOpen(true);
    } else {
      isAnimatingRef.current = true;

      const tl = gsap.timeline();

      tl.to(menuOverlay, {
        clipPath: "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)",
        duration: 1,
        ease: "hop",
      })
        .to(menuOverlayContent, { yPercent: -50, duration: 1, ease: "hop" }, "<")
        .to(copyContainers, { opacity: 0.25, duration: 1, ease: "hop" }, "<");

      tl.call(() => {
        splitByContainer.forEach((containerSplits) => {
          const copyLines = containerSplits.flatMap((split) => split.lines);
          gsap.set(copyLines, { y: "-110%" });
        });

        gsap.set(copyContainers, { opacity: 1 });
        gsap.set(menuMediaWrapper, { opacity: 0 });

        isAnimatingRef.current = false;
        lenisRef.current?.start();
      });

      isMenuOpenRef.current = false;
      setIsMenuOpen(false);
    }
  };

  // Trigger — plain "Menu" pill. Only opens the overlay; fades out once
  // open so it doesn't sit on top of / compete with the overlay's own
  // close button.
  const toggleButton = (
    <div
      ref={menuToggleBtnRef}
      onClick={handleToggle}
      role="button"
      tabIndex={0}
      aria-expanded={isMenuOpen}
      aria-label="Open menu"
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleToggle();
        }
      }}
      className={`py-4 px-8 w-fit rounded-full text-white font-bold lg:text-lg bg-primary hover:bg-accent transition-colors duration-300${
        isMenuOpen ? "pointer-events-none opacity-0" : "opacity-100"
      }`}
    > 
    <p>Schedule Meeting</p>
     
    </div>
  );

  // Close control — lives inside the overlay itself (top-right corner),
  // instead of being morphed from the "Menu" pill above.
  const closeButton = (
    <button
      type="button"
      onClick={handleToggle}
      aria-label="Close menu"
      className="fixed right-2 lg:right-28 top-2 lg:top-12 z-[10000] flex h-12 w-12 cursor-pointer items-center justify-center hover:bg-primary rounded-full border  border-white/10 bg-transparent "
    >
      <span className="absolute h-[1.25px] w-[15px] origin-center rotate-45 scale-x-105 bg-white" />
      <span className="absolute h-[1.25px] w-[15px] origin-center -rotate-45 scale-x-105 bg-white" />
    </button>
  );

  // Only attempt to mount Spline if WebGL is actually available and it
  // hasn't already errored out at runtime. Otherwise we just show the
  // "Let's Talk" heading on its own, so a missing/disabled WebGL context
  // never breaks the overlay or spams the console.
  const showSpline = webglOk && !splineError;

  const overlay = (
    <>
      <style jsx global>{`
        @import url("https://fonts.cdnfonts.com/css/pp-neue-montreal");

        .menu-split-line {
          position: relative;
          will-change: transform;
        }
      `}</style>

      <div
        ref={menuOverlayRef}
        className="fixed left-0 top-0 z-[9999] h-[100svh] w-screen overflow-hidden bg-secondary text-white will-change-[clip-path]"
        style={{ clipPath: "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)" }}
      >
        {closeButton}

        <div
          ref={menuOverlayContentRef}
          className="pointer-events-auto fixed left-0 top-0 flex h-[100svh] w-screen -translate-y-1/2 overflow-hidden will-change-transform"
        >
          {/* Media column */}
          <div
            ref={menuMediaWrapperRef}
            className="relative flex-[2] opacity-0 will-change-[opacity] max-[1000px]:hidden bg-secondary "
          >
            {showSpline ? (
              <Spline
                scene="https://prod.spline.design/BNaurVSeS57NeyWI/scene.splinecode"
                onLoad={onSplineLoad}
                onError={() => setSplineError(true)}
                style={{ background: "transparent" }}
              />
            ) : null}
            <h2 className="absolute inset-0 z-10 flex items-center justify-center text-center text-primary font-bold text-9xl pointer-events-none">
              Let's Talk
            </h2>
          </div>

          {/* Content column */}
          <div className="relative flex flex-[3] flex-col justify-between overflow-y-auto">
            <div className="absolute left-1/2 top-1/2 flex w-3/4 -translate-x-1/2 -translate-y-1/2 items-end gap-8 p-8 max-[1000px]:w-full max-[1000px]:flex-col max-[1000px]:items-start max-[1000px]:gap-20">
              <div className="flex-1 flex flex-col gap-3 mt-24 lg:mt-0">
                <h2 className="text-white text-4xl lg:text-7xl mb-4 lg:mb-16 font-bold">
                  Tap Into the Extraordinary
                </h2>

                <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                  <div className="flex gap-2 w-full flex-wrap lg:flex-nowrap">
                    <div className="w-full rounded-3xl bg-secondary">
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Full name *"
                        className="w-full bg-transparent border border-white text-white placeholder:text-white px-4 py-3 rounded-3xl outline-none"
                      />
                    </div>
                    <div className="w-full rounded-3xl bg-secondary">
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder="Company"
                        className="w-full bg-transparent border border-white text-white placeholder:text-white px-4 py-3 rounded-3xl outline-none"
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 w-full flex-wrap lg:flex-nowrap">
                    <div className="w-full rounded-3xl bg-secondary">
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Email *"
                        className="w-full bg-transparent border border-white text-white placeholder:text-white px-4 py-3 rounded-3xl outline-none"
                      />
                    </div>
                    <div className="w-full rounded-3xl bg-secondary">
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="Phone *"
                        className="w-full bg-transparent border border-white text-white placeholder:text-white px-4 py-3 rounded-3xl outline-none"
                      />
                    </div>
                  </div>

                  <div className="rounded-3xl bg-secondary">
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={6}
                      placeholder="Project Details *"
                      className="w-full bg-transparent border border-white text-white placeholder:text-white px-4 py-3 rounded-3xl outline-none resize-none"
                    />
                  </div>

                  {/* CAPTCHA - only shows after first submit attempt */}
                  {showCaptcha && (
                    <div className="items-center flex flex-col gap-3 py-2">
                      <div className="bg-white/90 px-4 py-2 w-60 rounded-3xl shadow-sm flex justify-around items-center gap-2">
                        <p className="text-xl font-bold text-center text-secondary font-mono tracking-wider">
                          {captcha.question}
                        </p>
                        <FiRefreshCw
                          size={20}
                          className="text-secondary cursor-pointer"
                          onClick={() => {
                            generateCaptcha();
                            setCaptchaInput("");
                          }}
                        />
                      </div>

                      <input
                        type="number"
                        value={captchaInput}
                        onChange={handleCaptchaChange}
                        placeholder="Answer"
                        className={`px-4 w-60 py-2 rounded-3xl outline-none transition-all text-white bg-secondary border ${
                          captchaInput === ""
                            ? "border-white/30"
                            : captchaValid
                            ? "border-green-400"
                            : "border-red-400"
                        }`}
                      />
                    </div>
                  )}

                  {status.message && (
                    <p
                      className={`text-xs md:text-sm text-center font-medium ${
                        status.type === "success"
                          ? "text-green-400"
                          : status.type === "error"
                          ? "text-red-400"
                          : "text-white"
                      }`}
                    >
                      {status.message}
                    </p>
                  )}

                  <div>
                    <button
                      type="submit"
                      disabled={loading || (showCaptcha && !captchaValid)}
                      className="w-full items-center py-3 rounded-3xl bg-white font-semibold text-secondary hover:bg-accent hover:text-white disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Sending...
                        </span>
                      ) : showCaptcha && !captchaValid ? (
                        "Solve CAPTCHA"
                      ) : (
                        "Submit"
                      )}
                    </button>
                  </div>
                  <p className="text-xs md:text-sm text-white mt-8">
                    By submitting this form, you agree to receive
                    communications from Ignite Visibility, including marketing
                    emails and SMS messages if applicable. You also agree to
                    our{" "}
                    <Link href={"/privacy-policy"}>
                     <span className="text-accent underline">
                      Privacy Policy
                    </span> 
                    </Link>
                  
                    ,{" "}
                     <Link href={"/terms-and-conditions"}>
                    <span className="text-accent underline">
                      Terms of Service
                    </span>
                    </Link>
                    {" "}
                    and{" "}
                     <Link href={"/cookies-policy"}>
                    <span className="text-accent underline">Cookies Policy</span>.
                    </Link>
                  </p>
                </form>
              </div>
            </div>

            <div className="mx-auto flex w-3/4  items-end gap-8 p-8 mt-10 lg:mt-0 max-[1000px]:w-full">
              <div ref={addColRef} className="flex flex-[3] flex-col gap-2">
                <p className="text-[0.95rem] font-medium capitalize text-white">
                  free consultation
                </p>
              </div>
              <div
                ref={addColRef}
                className="flex flex-[2] flex-col  items-center gap-1"
              >
                <p className="text-[0.95rem] font-medium text-white">
                  213-772-057-037
                </p>
                <p className="text-[0.95rem] font-medium text-white">
                  contact@evinov.com
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      {toggleButton}
      {mounted ? createPortal(overlay, document.body) : null}
    </>
  );
}